import {inject, injectable} from "inversify";
import {action, configure, makeObservable, observable} from "mobx";
import {
    GetReportTicketAggregateUseCase
} from "@dropDesk/domain/use_case/reports/report_ticket_aggregate/get_report_ticket_aggregate.usecase";
import {
    LineChartReportTicketAggregate
} from "@dropDesk/domain/entities/reports/report_ticket_aggregate/line_chart_report_ticket_agreggate";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {translate} from "@dropDesk/storage/i18n/translate_helper";
import {getEndHourCurrentDate, getInitialHourCurrentDate} from "@dropDesk/utils/helpers/date_helper";
import {
    ExportHtmlReportTicketAggregateUseCase
} from "@dropDesk/domain/use_case/reports/report_ticket_aggregate/export_html_report_ticket_aggregate.usecase";
import {
    getPeriodAndLabelDisplay,
    PeriodEnum,
    setPeriodAndLabelDisplay
} from "@dropDesk/utils/helpers/common";
import {
    ComparativeNormalization
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/comparative_metric.entity";
import {
    ComparativeMetricsGroup,
    ComparativeTexts
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/report_ticket_comparative.entity";
import {
    ReportTicketAggregateEntity
} from "@dropDesk/domain/entities/reports/report_ticket_aggregate/report_ticket_aggregate.entity";
import {
    buildMetricsFromAggregate
} from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_aggregate/controller/build_metrics_from_aggregate";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import {ReportEntityFilters} from "@dropDesk/presentation/components/filters/report_entity_filters";

configure({
    enforceActions: "always",
});

@injectable()
export class ReportTicketAggregateController {
    private readonly _getReportTicketAggregate: GetReportTicketAggregateUseCase;
    private readonly _exportHtmlReportTicketAggregateUseCase: ExportHtmlReportTicketAggregateUseCase;

    constructor(
        @inject(GetReportTicketAggregateUseCase) getReportTicketAggregate: GetReportTicketAggregateUseCase,
        @inject(ExportHtmlReportTicketAggregateUseCase) exportHtmlReportTicketAggregateUseCase: ExportHtmlReportTicketAggregateUseCase,
    ) {
        makeObservable(this);
        this._getReportTicketAggregate = getReportTicketAggregate;
        this._exportHtmlReportTicketAggregateUseCase = exportHtmlReportTicketAggregateUseCase;

    }


    @observable
    lineChartClientsAggregate: LineChartReportTicketAggregate[] | undefined

    @observable
    lineChartSectorsAggregate: LineChartReportTicketAggregate[] | undefined

    @observable
    lineChartUsersAggregate: LineChartReportTicketAggregate[] | undefined

    @observable
    lineChartDescriptionsStaticAggregate: LineChartReportTicketAggregate[] | undefined

    @observable
    timeFirstResponseAggregate: number = 0;

    @observable
    timeResolutionAggregate: number = 0;

    @observable
    totalTickets: number = 0;

    @observable
    averageRating: number = 0;

    @observable
    ticketsInBusinessHour: number = 0;

    @observable
    ticketsOutBusinessHour: number = 0;

    @observable
    metrics: ComparativeMetricsGroup | null = null;

    @observable
    normalization: ComparativeNormalization = 'daily';

    @observable
    texts: ComparativeTexts | null = null;

    @observable
    filterClient: ClientEntity | null = null;

    @observable
    filterUserClient: UserEntity | null = null;

    @observable
    filterSector: SectorEntity | null = null;

    @observable
    filterAttendant: UserEntity | null = null;

    @observable
    filterDescriptionStatic: TicketsDescriptionStaticEntity | null = null;

    @observable
    period: Array<Date> | null = getPeriodAndLabelDisplay().range;

    @observable
    labelPeriod: PeriodEnum = getPeriodAndLabelDisplay().label;

    @observable
    loading = false;

    @action
    setFilterClient(client: ClientEntity | null) {
        this.filterClient = client;
        if (!client) {
            this.filterUserClient = null;
        }
        this.getDataReportTicketAggregate();
    }

    @action
    setFilterUserClient(contact: UserEntity | null) {
        this.filterUserClient = contact;
        this.getDataReportTicketAggregate();
    }

    @action
    setFilterSector(sector: SectorEntity | null) {
        this.filterSector = sector;
        this.getDataReportTicketAggregate();
    }

    @action
    setFilterAttendant(attendant: UserEntity | null) {
        this.filterAttendant = attendant;
        this.getDataReportTicketAggregate();
    }

    @action
    setFilterDescriptionStatic(descriptionStatic: TicketsDescriptionStaticEntity | null) {
        this.filterDescriptionStatic = descriptionStatic;
        this.getDataReportTicketAggregate();
    }

    @action
    clearEntityFilters() {
        this.filterClient = null;
        this.filterUserClient = null;
        this.filterSector = null;
        this.filterAttendant = null;
        this.filterDescriptionStatic = null;
        this.getDataReportTicketAggregate();
    }

    private buildEntityFilters(): ReportEntityFilters | undefined {
        const filters: ReportEntityFilters = {};
        if (this.filterClient) {
            filters.idClient = this.filterClient.id;
        }
        if (this.filterUserClient) {
            filters.idContact = this.filterUserClient.id;
        }
        if (this.filterSector) {
            filters.idSector = this.filterSector.id;
        }
        if (this.filterAttendant) {
            filters.idUser = this.filterAttendant.id;
        }
        if (this.filterDescriptionStatic) {
            filters.idDescriptionStatic = this.filterDescriptionStatic.id;
        }
        return Object.keys(filters).length > 0 ? filters : undefined;
    }

    @action
    setPeriod(period: Array<Date> | null, label: PeriodEnum) {
        this.period = period;
        this.labelPeriod = label;
        setPeriodAndLabelDisplay({
            range: period!,
            label: label
        });
    }

    @action
    hasData(chart?: LineChartReportTicketAggregate[]): boolean {
        return !!chart && chart.filter(entry => entry.y > 0).length > 0;
    }


    @action
    setLineChartClientsAggregate(lineChartClientsAggregate: LineChartReportTicketAggregate[]) {
        this.lineChartClientsAggregate = lineChartClientsAggregate;
    }


    @action
    setLineChartSectorsAggregate(lineChartSectorsAggregate: LineChartReportTicketAggregate[]) {
        this.lineChartSectorsAggregate = lineChartSectorsAggregate;
    }

    @action
    setLineChartUsersAggregate(lineChartUsersAggregate: LineChartReportTicketAggregate[]) {
        this.lineChartUsersAggregate = lineChartUsersAggregate;
    }

    @action
    setLineChartDescriptionsStaticAggregate(lineChartDescriptionsStaticAggregate: LineChartReportTicketAggregate[]) {
        this.lineChartDescriptionsStaticAggregate = lineChartDescriptionsStaticAggregate;
    }

    @action
    setTimeFirstResponseAggregate(timeFirstResponseAggregate: number) {
        this.timeFirstResponseAggregate = timeFirstResponseAggregate;
    }

    @action
    setTimeResolutionAggregate(timeResolutionAggregate: number) {
        this.timeResolutionAggregate = timeResolutionAggregate;
    }

    @action
    setTotalTickets(totalTickets: number) {
        this.totalTickets = totalTickets;
    }

    @action
    setAverageRating(averageRating: number) {
        this.averageRating = averageRating;
    }

    @action
    setTicketsInBusinessHour(ticketsInBusinessHour: number) {
        this.ticketsInBusinessHour = ticketsInBusinessHour;
    }

    @action
    setTicketsOutBusinessHour(ticketsOutBusinessHour: number) {
        this.ticketsOutBusinessHour = ticketsOutBusinessHour;
    }

    @action
    setMetrics(metrics: ComparativeMetricsGroup | null) {
        this.metrics = metrics;
    }

    @action
    setNormalization(normalization: ComparativeNormalization) {
        this.normalization = normalization;
    }

    @action
    setTexts(texts: ComparativeTexts | null) {
        this.texts = texts;
    }

    @observable
    loadingMessage?: string | null = null;

    @action
    setLoadingMessage(message?: string | null) {
        this.loadingMessage = message;
    }

    @action
    startLoading(loadingMessage?: string | null) {
        this.setLoadingMessage(loadingMessage);
        this.loading = true;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.setLoadingMessage(null);
    }

    @action
    initialize() {
        this.getDataReportTicketAggregate();
    }

    @action
    setAllDataReportTicketAggregate(response: ReportTicketAggregateEntity) {
        this.setLineChartClientsAggregate(response.lineChartClientsAggregate);
        this.setLineChartSectorsAggregate(response.lineChartSectorsAggregate);
        this.setLineChartUsersAggregate(response.lineChartUsersAggregate);
        this.setLineChartDescriptionsStaticAggregate(response.lineChartDescriptionsStaticAggregate);
        this.setTimeFirstResponseAggregate(response.timeFirstResponseAggregate);
        this.setTimeResolutionAggregate(response.timeResolutionAggregate);
        this.setTotalTickets(response.totalTickets);
        this.setAverageRating(response.averageRating);
        this.setTicketsInBusinessHour(response.ticketsInBusinessHour);
        this.setTicketsOutBusinessHour(response.ticketsOutBusinessHour);
        this.setTexts(response.texts);

        if (this.period && this.period[0] && this.period[1]) {
            const {metrics, normalization} = buildMetricsFromAggregate(response, this.period);
            this.setMetrics(metrics);
            this.setNormalization(normalization);
        }
    }

    @action
    removeAllData() {
        this.setLineChartClientsAggregate([]);
        this.setLineChartSectorsAggregate([]);
        this.setLineChartUsersAggregate([]);
        this.setLineChartDescriptionsStaticAggregate([]);
        this.setTimeFirstResponseAggregate(0);
        this.setTimeResolutionAggregate(0);
        this.setTotalTickets(0);
        this.setAverageRating(0);
        this.setTicketsInBusinessHour(0);
        this.setTicketsOutBusinessHour(0);
        this.setMetrics(null);
        this.setNormalization('daily');
        this.setTexts(null);
    }

    @action
    getDataReportTicketAggregate = async (): Promise<void> => {
        try {
            if (this.period && this.period[0] && this.period[1]) {
                this.removeAllData();
                this.startLoading();
                const response = await this._getReportTicketAggregate.call({
                    startDate: getInitialHourCurrentDate(this.period[0]),
                    endDate: getEndHourCurrentDate(this.period[1])
                }, this.buildEntityFilters());
                this.setAllDataReportTicketAggregate(response);
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };


    @action
    exportHtml = async (onSucess: (key: string) => void): Promise<void> => {
        try {
            if (this.period && this.period[0] && this.period[1]) {
                this.startLoading("report_ticket_aggregate.export_html");
                await this._exportHtmlReportTicketAggregateUseCase.call(
                    {
                        startDate: getInitialHourCurrentDate(this.period[0]),
                        endDate: getEndHourCurrentDate(this.period[1])
                    },
                    this.buildEntityFilters(),
                );
                onSucess("report_ticket_aggregate.success.export_html");
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };

}
